import React, { useState } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail
} from "firebase/auth";
import { auth } from "../../firebase";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, User, Gavel, AlertCircle, Sparkles } from "lucide-react";
import googleIcon from "../../Assets/google-icon.svg";
import "./Login.css";

function Login() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [profession, setProfession] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const navigate = useNavigate();

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setError("");
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
      navigate("/");
    } catch (err) {
      console.error("Google auth error:", err);
      setError(err.message || "Failed to authenticate with Google.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      if (forgotPasswordMode) {
        if (!email) {
          setError("Please enter your email to receive recovery instructions.");
          setIsLoading(false);
          return;
        }
        await sendPasswordResetEmail(auth, email);
        setResetSent(true);
        setIsLoading(false);
        return;
      }

      if (isRegister) {
        if (!email || !password) {
          setError("Email and password are required.");
          setIsLoading(false);
          return;
        }
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      navigate("/");
    } catch (err) {
      console.error("Email auth error:", err);
      let userFriendly = err.message;
      if (err.code === "auth/wrong-password" || err.code === "auth/user-not-found" || err.code === "auth/invalid-credential") {
        userFriendly = "Invalid email or password credentials.";
      } else if (err.code === "auth/email-already-in-use") {
        userFriendly = "This email is already registered. Please sign in.";
      } else if (err.code === "auth/weak-password") {
        userFriendly = "Password must be at least 6 characters.";
      }
      setError(userFriendly);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="court-login-page">
      <div className="login-backdrop-glow"></div>
      
      <div className="court-login-card">
        {/* Card Header */}
        <div className="login-card-header">
          <div className="login-gavel-seal">
            <Gavel size={28} />
          </div>
          <h2>The Kangaroo Court</h2>
          <span className="login-card-subtitle">
            {forgotPasswordMode 
              ? "Recover Bar Credentials" 
              : isRegister 
                ? "Apply for Bar Admission" 
                : "Enter the Chamber"}
          </span>
        </div>

        {/* Error / Status Alert */}
        {error && (
          <div className="login-alert-box error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {resetSent && (
          <div className="login-alert-box success">
            <span>Password recovery link dispatched to {email}. Check your inbox.</span>
          </div>
        )}

        {/* Quick Google Sign-In */}
        {!forgotPasswordMode && (
          <>
            <button
              type="button"
              className="btn-google-auth"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
            >
              <img src={googleIcon} alt="Google" className="google-btn-icon" />
              <span>Continue with Google Account</span>
            </button>

            <div className="login-divider-row">
              <div className="divider-line"></div>
              <span>OR ENTER VIA EMAIL</span>
              <div className="divider-line"></div>
            </div>
          </>
        )}

        {/* Email & Password Form */}
        <form onSubmit={handleEmailAuth} className="court-auth-form">
          {isRegister && !forgotPasswordMode && (
            <>
              <div className="auth-field-group">
                <label>Full Name / Alias</label>
                <div className="input-with-icon">
                  <User size={16} className="field-icon" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Dr. Jane / Anonymous Editor"
                    required
                  />
                </div>
              </div>

              <div className="auth-field-group">
                <label>Field / Profession</label>
                <div className="input-with-icon">
                  <Sparkles size={16} className="field-icon" />
                  <input
                    type="text"
                    value={profession}
                    onChange={(e) => setProfession(e.target.value)}
                    placeholder="e.g. Video Editor, Architecture, Medicine, Dev"
                  />
                </div>
              </div>
            </>
          )}

          <div className="auth-field-group">
            <label>Email Address</label>
            <div className="input-with-icon">
              <Mail size={16} className="field-icon" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="juror@syndicate.internal"
                required
              />
            </div>
          </div>

          {!forgotPasswordMode && (
            <div className="auth-field-group">
              <div className="field-label-split">
                <label>Password</label>
                <button
                  type="button"
                  className="btn-text-link"
                  onClick={() => setForgotPasswordMode(true)}
                >
                  Forgot?
                </button>
              </div>
              <div className="input-with-icon">
                <Lock size={16} className="field-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  className="btn-toggle-eye"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          )}

          <button
            type="submit"
            className="btn-court-submit"
            disabled={isLoading}
          >
            {isLoading 
              ? "Verifying Credentials..." 
              : forgotPasswordMode 
                ? "Send Recovery Link" 
                : isRegister 
                  ? "Submit Admission Application" 
                  : "Sign In to Docket"}
          </button>
        </form>

        {/* Footer Mode Switcher */}
        <div className="login-card-footer">
          {forgotPasswordMode ? (
            <button
              type="button"
              className="btn-switch-mode"
              onClick={() => { setForgotPasswordMode(false); setResetSent(false); }}
            >
              Return to Sign In
            </button>
          ) : (
            <p>
              {isRegister ? "Already admitted to the Bar?" : "New to the syndicate?"}{" "}
              <button
                type="button"
                className="btn-switch-mode"
                onClick={() => { setIsRegister(!isRegister); setError(""); }}
              >
                {isRegister ? "Sign In" : "Apply for Admission"}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;
